import { ROLE_CONFIG } from "./constants";

/**
 * Utilidades de formateo
 */

const APP_TIMEZONE = "America/Mexico_City";
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parsea un valor "solo día" a un Date local (Y/M/D) sin desfase.
 * - "YYYY-MM-DD" → ese día local
 * - Medianoche UTC (legado) → Y/M/D UTC
 * - Otro instante (p. ej. medianoche CDMX) → día calendario en America/Mexico_City
 */
export const parseDateOnly = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string") {
    const m = value.trim().match(DATE_ONLY_RE);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const isUtcMidnight =
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  if (isUtcMidnight) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = Number(parts.find((p) => p.type === "year").value);
  const m = Number(parts.find((p) => p.type === "month").value);
  const day = Number(parts.find((p) => p.type === "day").value);
  return new Date(y, m - 1, day);
};

/**
 * Formatea una fecha solo-día (seguimientos, vencimientos, ingreso, etc.)
 * evitando el bug de "un día antes" en zonas UTC-*.
 */
export const formatDateOnly = (value, options = {}) => {
  if (!value) return "—";
  const d = parseDateOnly(value);
  if (!d) return "—";
  // parseDateOnly ya dejó un Date local con el Y/M/D correcto
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...options,
  });
};

/** Valor yyyy-mm-dd para inputs type="date" desde un ISO/Date solo-día. */
export const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && DATE_ONLY_RE.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const isUtcMidnight =
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  const y = isUtcMidnight ? d.getUTCFullYear() : d.getFullYear();
  const m = String((isUtcMidnight ? d.getUTCMonth() : d.getMonth()) + 1).padStart(2, "0");
  const day = String(isUtcMidnight ? d.getUTCDate() : d.getDate()).padStart(2, "0");
  // Si el backend ya guardó medianoche CDMX (06:00Z), preferir día en APP_TIMEZONE
  if (!isUtcMidnight) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: APP_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const yy = parts.find((p) => p.type === "year")?.value;
    const mm = parts.find((p) => p.type === "month")?.value;
    const dd = parts.find((p) => p.type === "day")?.value;
    if (yy && mm && dd) return `${yy}-${mm}-${dd}`;
  }
  return `${y}-${m}-${day}`;
};

/**
 * Obtiene la configuración de un rol
 * @param {string} role - Rol del usuario
 * @returns {Object} Configuración del rol
 */
export const getRoleConfig = (role) => {
  const roleKey = (role || "").toLowerCase();
  return ROLE_CONFIG[roleKey] || ROLE_CONFIG.default;
};

/**
 * Formatea un número con separadores de miles
 * @param {number} num - Número a formatear
 * @returns {string} Número formateado
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "N/D";
  return new Intl.NumberFormat("es-MX").format(num);
};

/**
 * Formatea una fecha relativa (ej: "hace 2 horas")
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now - then) / 1000);

  if (diffInSeconds < 60) return "hace un momento";
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `hace ${minutes} minuto${minutes > 1 ? "s" : ""}`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `hace ${hours} hora${hours > 1 ? "s" : ""}`;
  }
  const days = Math.floor(diffInSeconds / 86400);
  return `hace ${days} día${days > 1 ? "s" : ""}`;
};

/**
 * Trunca un texto largo
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitaliza la primera letra de una cadena
 * @param {string} str - Cadena a capitalizar
 * @returns {string} Cadena capitalizada
 */
export const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Obtiene las iniciales de un nombre
 * @param {string} name - Nombre completo
 * @returns {string} Iniciales
 */
export const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Convierte una hora en formato HH:mm a minutos desde medianoche
 * @param {string} timeString - Hora en formato HH:mm
 * @returns {number} Minutos desde medianoche
 */
export const timeToMinutes = (timeString) => {
  if (!timeString) return null;
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

